import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Search, MapPin, Briefcase, Clock, DollarSign, Upload, ChevronRight, GraduationCap, Building } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JobsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Job categories with images
  const jobCategories = [
    { id: 'sales', name: i18n.language === 'ar' ? 'المبيعات / تطوير الأعمال' : 'Sales / Business Development', count: '260+', image: '💼' },
    { id: 'driver', name: i18n.language === 'ar' ? 'سائق / توصيل' : 'Driver / Delivery', count: '170+', image: '🚗' },
    { id: 'accounting', name: i18n.language === 'ar' ? 'المحاسبة / المالية' : 'Accounting / Finance', count: '140+', image: '📊' },
    { id: 'realestate', name: i18n.language === 'ar' ? 'العقارات' : 'Real Estate', count: '80+', image: '🏠' },
  ];

  // Qualifications
  const qualifications = [
    { id: 'highschool', name: i18n.language === 'ar' ? 'ثانوية عامة' : 'High School/Secondary', count: '1,000+', icon: GraduationCap },
    { id: 'bachelors', name: i18n.language === 'ar' ? 'بكالوريوس' : 'Bachelors Degree', count: '1,600+', icon: GraduationCap },
    { id: 'masters', name: i18n.language === 'ar' ? 'ماجستير' : 'Masters Degree', count: '500+', icon: GraduationCap },
    { id: 'phd', name: i18n.language === 'ar' ? 'دكتوراه' : 'PHD', count: '100+', icon: GraduationCap },
  ];

  // Job types
  const jobTypes = [
    { id: 'fulltime', name: i18n.language === 'ar' ? 'دوام كامل' : 'Full time', count: '2,500+', icon: Clock },
    { id: 'parttime', name: i18n.language === 'ar' ? 'دوام جزئي' : 'Part time', count: '300+', icon: Clock },
    { id: 'contract', name: i18n.language === 'ar' ? 'عقد' : 'Contract', count: '200+', icon: Briefcase },
    { id: 'remote', name: i18n.language === 'ar' ? 'عن بعد' : 'Remote', count: '150+', icon: Building },
  ];

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      // Fetch jobs from listings with jobs category
      const res = await axios.get(`${API}/listings?search=job`);
      setJobs(res.data.slice(0, 5));
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}&category=jobs`);
    }
  };

  // Sample popular jobs
  const popularJobs = [
    { title: 'Sales Executive', company: 'ABC Company', type: 'Full Time', salary: 'Negotiable', location: 'Dubai' },
    { title: 'Delivery Driver', company: 'XYZ Logistics', type: 'Full Time', salary: 'AED 4,000', location: 'Abu Dhabi' },
    { title: 'Accountant', company: 'Finance Corp', type: 'Full Time', salary: 'AED 8,000', location: 'Sharjah' },
    { title: 'Real Estate Agent', company: 'Property Plus', type: 'Contract', salary: 'Commission', location: 'Dubai' },
  ];

  return (
    <div className="min-h-screen bg-white pb-24" data-testid="jobs-page">
      {/* Header with Logo */}
      <div className="bg-white border-b border-gray-100 py-4 px-4">
        <div className="flex justify-center mb-4">
          <span className="text-2xl font-bold">
            <span className="text-gray-900">Findadz</span>
          </span>
        </div>
        
        {/* Hero Text */}
        <h1 className="text-2xl font-bold text-center mb-4">
          <span className="text-gray-900">{i18n.language === 'ar' ? 'البحث عن وظيفة أصبح سهلاً مع ' : 'Job hunting '}</span>
          <span className="text-[#E31C25]">{i18n.language === 'ar' ? 'فايند أدز' : 'made easy'}</span>
          <span className="text-gray-900">{i18n.language === 'ar' ? '' : ' with Findadz'}</span>
        </h1>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder={i18n.language === 'ar' ? 'ابحث عن مهارات، شركة أو عنوان...' : 'Search for skills, company or title...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg outline-none focus:border-[#E31C25]"
              data-testid="job-search-input"
            />
          </div>
          <button
            type="submit"
            className="px-4 py-3 bg-[#E31C25] text-white rounded-lg"
            data-testid="job-search-btn"
          >
            <Search className="w-5 h-5" />
          </button>
        </form>
      </div>

      <div className="px-4 py-6 space-y-8">
        {/* Popular Jobs */}
        <section>
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            {i18n.language === 'ar' ? 'الوظائف الشائعة' : 'Popular Jobs'}
          </h2>
          <div className="space-y-3">
            {popularJobs.map((job, index) => (
              <div
                key={index}
                onClick={() => navigate('/search?category=jobs')}
                className="bg-white border border-gray-100 rounded-xl p-4 cursor-pointer hover:shadow-md transition-shadow"
                data-testid={`job-card-${index}`}
              >
                <h3 className="font-semibold text-gray-900 mb-1">{job.title}</h3>
                <p className="text-sm text-gray-600 mb-2">{job.company}</p>
                <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Briefcase className="w-3 h-3" />
                    {job.type}
                  </span>
                  <span className="flex items-center gap-1">
                    <DollarSign className="w-3 h-3" />
                    {job.salary}
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {job.location}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Jobs By Category */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900">
              {i18n.language === 'ar' ? 'الوظائف حسب الفئة' : 'Jobs By Category'}
            </h2>
            <button 
              onClick={() => navigate('/search?category=jobs')}
              className="text-[#E31C25] text-sm font-medium"
            >
              {i18n.language === 'ar' ? 'عرض الكل' : 'View all'}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {jobCategories.map(cat => (
              <button
                key={cat.id}
                onClick={() => navigate(`/search?category=jobs&subcategory=${cat.id}`)}
                className="bg-blue-50 rounded-xl p-4 text-left hover:bg-blue-100 transition-colors"
                data-testid={`job-category-${cat.id}`}
              >
                <div className="text-2xl mb-2">{cat.image}</div>
                <p className="font-medium text-gray-900 text-sm mb-1">{cat.name}</p>
                <p className="text-xs text-blue-600">{cat.count} Jobs</p>
              </button>
            ))}
          </div>
        </section>

        {/* Jobs by Qualification */}
        <section>
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            {i18n.language === 'ar' ? 'الوظائف حسب المؤهل في الإمارات' : 'Jobs by Qualification in UAE'}
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {qualifications.map(qual => {
              const Icon = qual.icon;
              return (
                <button
                  key={qual.id}
                  onClick={() => navigate(`/search?category=jobs&qualification=${qual.id}`)}
                  className="bg-gray-50 rounded-xl p-4 flex items-center gap-3 hover:bg-gray-100 transition-colors"
                  data-testid={`qualification-${qual.id}`}
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900 text-sm">{qual.name}</p>
                    <p className="text-xs text-gray-500">{qual.count} Jobs</p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Jobs by Type */}
        <section>
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            {i18n.language === 'ar' ? 'الوظائف حسب النوع في الإمارات' : 'Jobs by Type in UAE'}
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {jobTypes.map(type => {
              const Icon = type.icon;
              return (
                <button
                  key={type.id}
                  onClick={() => navigate(`/search?category=jobs&type=${type.id}`)}
                  className="bg-gray-50 rounded-xl p-4 flex items-center gap-3 hover:bg-gray-100 transition-colors"
                  data-testid={`job-type-${type.id}`}
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Icon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="text-left">
                    <p className="font-medium text-gray-900 text-sm">{type.name}</p>
                    <p className="text-xs text-gray-500">{type.count} Jobs</p>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        {/* Upload CV Button */}
        <div className="pt-4">
          <button
            onClick={() => alert(i18n.language === 'ar' ? 'ميزة رفع السيرة الذاتية قريباً' : 'Upload CV feature coming soon!')}
            className="w-full py-3.5 bg-[#E31C25] text-white rounded-lg font-semibold flex items-center justify-center gap-2"
            data-testid="upload-cv-btn"
          >
            <Upload className="w-5 h-5" />
            {i18n.language === 'ar' ? 'رفع السيرة الذاتية' : 'Upload CV'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default JobsPage;
